import React from 'react';

/**
 * ProducerSmartButtons — Context-aware AI action buttons
 * They change based on production stage and send messages to chat.
 */

const BUTTON_GROUPS = {
  // No project yet — choose energy/mood
  none: [
    {
      group: 'Energía',
      buttons: [
        { label: 'Oscuro y denso',     msg: 'Quiero algo oscuro y denso, con mucha atmósfera',      color: '#7000FF' },
        { label: 'Energía total',      msg: 'Quiero energía al máximo, hard y directo',               color: '#FF6B00' },
        { label: 'Romántico y suave',  msg: 'Algo romántico, suave, que llegue al corazón',          color: '#FF0066' },
        { label: 'Chill y ambient',    msg: 'Algo muy chill, ambient, para relajarse totalmente',    color: '#00D4FF' },
      ],
    },
    {
      group: 'Estilo',
      buttons: [
        { label: 'Trap',         msg: 'Hazlo trap, 808s profundos, hi-hats rápidos',           color: '#7000FF' },
        { label: 'Reggaetón',    msg: 'Dembow clásico, reggaetón con flow latino',             color: '#FF6B00' },
        { label: 'Electrónico',  msg: 'Electrónica moderna, synths fuertes, drop potente',    color: '#00D4FF' },
        { label: 'Pop hit',      msg: 'Un pop hit con gancho fuerte, que pegue en radio',     color: '#FF0066' },
        { label: 'R&B soul',     msg: 'R&B con soul, groovy, muy melódico',                   color: '#F5C842' },
        { label: 'Sorpréndeme',  msg: 'Sorpréndeme, hazlo único, algo que no haya escuchado', color: '#00FF88' },
      ],
    },
  ],

  // Base generated
  base: [
    {
      group: 'Sónico',
      buttons: [
        { label: '+ Velocidad',   msg: 'Súbele el BPM, quiero que vaya más rápido y con más urgencia', color: '#00D4FF' },
        { label: '- Velocidad',   msg: 'Bájale el BPM, quiero que sea más lento y pesado',             color: '#7000FF' },
        { label: '+ Graves',      msg: 'Más bajos, más 808, más presencia en graves',                  color: '#FF6B00' },
        { label: '+ Brillo',      msg: 'Más brillo, más aire en los agudos, que respire más',          color: '#F5C842' },
      ],
    },
    {
      group: 'Producción',
      buttons: [
        { label: 'Añade strings',   msg: 'Añade cuerdas orquestales, que dé un toque épico',        color: '#F5C842' },
        { label: 'Más oscuro',      msg: 'Hazlo mucho más oscuro, que dé miedo, atmósfera densa',   color: '#7000FF' },
        { label: 'Drop potente',    msg: 'El drop tiene que ser brutal, que te golpee en el pecho', color: '#FF6B00' },
        { label: 'Más ambiente',    msg: 'Más textura ambiental, que el espacio suene más grande',  color: '#00D4FF' },
        { label: 'Melodía fuerte',  msg: 'Quiero una melodía principal muy fuerte y pegadiza',      color: '#FF0066' },
        { label: 'Minimalista',     msg: 'Hazlo más minimalista, quita elementos, más espacio',     color: '#00FF88' },
      ],
    },
  ],

  // Voice added
  voice: [
    {
      group: 'Vocal',
      buttons: [
        { label: 'Cambia la letra',   msg: 'La letra no me convence, prueba algo diferente', color: '#F5C842' },
        { label: 'Más emotivo',       msg: 'Que la voz suene mucho más emotiva, casi al llanto', color: '#FF0066' },
        { label: 'Más agresivo',      msg: 'Que la voz sea más agresiva, directa, con actitud', color: '#FF6B00' },
        { label: 'Añade armonías',    msg: 'Añade armonías vocales, capas en el coro', color: '#00D4FF' },
      ],
    },
    {
      group: 'Ajustar',
      buttons: [
        { label: 'Más autotune',  msg: 'Más autotune, que suene más procesado y moderno', color: '#7000FF' },
        { label: 'Más natural',   msg: 'Que la voz suene más natural, menos procesada', color: '#00FF88' },
        { label: 'Gancho mejor',  msg: 'El gancho del coro no pega, dame algo más fuerte', color: '#FF0066' },
        { label: 'Cambiar tono',  msg: 'Sube o baja el tono de la voz, que encaje mejor', color: '#F5C842' },
      ],
    },
  ],

  // Improved — final polish
  improve: [
    {
      group: 'Acabado',
      buttons: [
        { label: 'Radio mix',      msg: 'Prepáralo para radio, compresión comercial, brillante', color: '#F5C842' },
        { label: 'Club version',   msg: 'Versión para club, extended, más energía en los drops', color: '#7000FF' },
        { label: 'Cinematic',      msg: 'Hazlo más cinematográfico, épico, con orquesta', color: '#00D4FF' },
        { label: 'Lo-fi mood',     msg: 'Dale un toque lo-fi, warm, retro, nostálgico', color: '#FF6B00' },
        { label: 'Masteriza ya',   msg: 'Masteriza esto para Spotify/streaming, que suene a nivel profesional', color: '#00FF88' },
      ],
    },
  ],
};

export default function ProducerSmartButtons({ stages, onSendMessage, disabled = false, lang = 'es' }) {
  // Determine active group
  let activeStage = 'none';
  if (stages.improved) activeStage = 'improve';
  else if (stages.voice_done) activeStage = 'voice';
  else if (stages.base_done) activeStage = 'base';

  const groups = BUTTON_GROUPS[activeStage] || BUTTON_GROUPS.none;

  return (
    <div className={`space-y-3 ${disabled ? 'opacity-50 pointer-events-none' : ''}`} data-testid="smart-buttons">
      {groups.map((group) => (
        <div key={group.group}>
          <p className="text-[9px] text-[#A1A1AA] uppercase tracking-wider mb-2 font-medium">{group.group}</p>
          <div className="flex flex-wrap gap-1.5">
            {group.buttons.map((btn) => (
              <button
                key={btn.label}
                data-testid={`smart-btn-${btn.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => onSendMessage(btn.msg)}
                className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all hover:opacity-90 active:scale-95"
                style={{
                  background: `${btn.color}20`,
                  color: btn.color,
                  border: `1px solid ${btn.color}40`,
                }}
              >
                {btn.label}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
